import React, { useCallback, useEffect, useRef, useState } from 'react'
import classNames from 'classnames'
import Icon from '@material-ui/core/Icon'
import OtpInput from 'react-otp-input'
import cls from './verify-phone-number-form.module.scss'
import Text from '@/shared/ui/Text/Text'
import { Button } from '@/shared/ui/buttons/Button/Button'
import { ModalFront } from '@/shared/ui/Modal/Modal'
import { getPhoneVerificationDescription } from '../lib/getPhoneVerificationDescription'
import mobileIcon from '~shared/assets/img/icons/mobile.svg'
import smsIcon from '~shared/assets/img/icons/sms.svg'

import {
  getDelayFromError,
  getVarsFromError,
  isCodeResendTooFrequentError,
  getMaskedPhoneNumber,
  normalizeVerificationCode,
  getSecondsUntilResend,
  getRecipientMaskByDeliveryType,
  getResendTooFrequentPopupText,
} from '../lib/verificationFormHelpers'

const deliveryIconNames = {
  email: 'email',
}

const deliveryIconImages = {
  'voice-call': mobileIcon,
  sms: smsIcon,
}

export const VerifyPhoneNumberForm = props => {
  const {
    className,
    onRequestCode,
    onVerifyCode,
    onVerifySuccess,
    labels = {},
    codeLength = 4,
    delayBeforeCodeResend = 60,
    requestCodeOnMount = false,
    resendButtonId = 'verify_phone_number_form_resend_button',
    textId = 'verify_phone_number_form_text',
    variant = 'page',
    voiceCallFirst = false,
    initialPhoneMask,
    initialRecipientMask,
    initialCodeRequestedAt,
    initialDeliveryType,
    initialAvailableDeliveryTypes = [],
    initialCanChooseAnotherMethod = false,
    hideDescription = false,
    leadingImageSrc,
    renderDescription,
    secondaryActionLabel,
    onSecondaryAction,
    onDeliveryMethodsVisibleChange,
    deliveryMethodsAsModal = false,
    showDeliveryMethodsOnMount = false,
    resendDeliveryType,
    onClose,
  } = props

  const [code, setCode] = useState('')
  const initialSecondsUntilResend = getSecondsUntilResend(
    initialCodeRequestedAt,
    delayBeforeCodeResend,
  )
  const initialCodeAlreadyRequested = Boolean(initialCodeRequestedAt)
  const shouldRestoreRequestedCode = initialSecondsUntilResend > 0
  const [secondsRemaining, setSecondsRemaining] = useState(
    shouldRestoreRequestedCode
      ? initialSecondsUntilResend
      : requestCodeOnMount
        ? delayBeforeCodeResend
        : 0,
  )
  const [codeInvalid, setCodeInvalid] = useState(false)
  const [verificationError, setVerificationError] = useState('')
  const [resendTooFrequentError, setResendTooFrequentError] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const firstDeliveryType = initialDeliveryType || (voiceCallFirst ? 'voice-call' : 'sms')
  const initialMaskedPhone = getMaskedPhoneNumber(initialPhoneMask)
  const [recipientMask, setRecipientMask] = useState(initialRecipientMask || initialMaskedPhone)
  const [phoneMask, setPhoneMask] = useState(initialMaskedPhone)
  const [emailMask, setEmailMask] = useState(
    initialDeliveryType === 'email' ? initialRecipientMask : '',
  )
  const [delay, setDelay] = useState(delayBeforeCodeResend)
  const [deliveryType, setDeliveryType] = useState(firstDeliveryType)
  const [availableDeliveryTypes, setAvailableDeliveryTypes] = useState(
    initialAvailableDeliveryTypes,
  )
  const [canChooseAnotherMethod, setCanChooseAnotherMethod] = useState(
    initialCanChooseAnotherMethod,
  )
  const [showDeliveryMethods, setShowDeliveryMethods] = useState(
    showDeliveryMethodsOnMount && !initialCodeAlreadyRequested,
  )

  const intervalRef = useRef(null)
  const requestOnMountHandledRef = useRef(false)
  const isSubmittingRef = useRef(false)
  const resendTooFrequentPopupText = getResendTooFrequentPopupText(labels)

  useEffect(() => {
    onDeliveryMethodsVisibleChange?.(showDeliveryMethods)
  }, [onDeliveryMethodsVisibleChange, showDeliveryMethods])

  useEffect(() => {
    return () => {
      onDeliveryMethodsVisibleChange?.(false)
    }
  }, [onDeliveryMethodsVisibleChange])

  useEffect(() => {
    setCode('')
  }, [codeLength])

  const clearTimer = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current)
      intervalRef.current = null
    }
  }, [])

  const startTimer = useCallback(
    seconds => {
      clearTimer()
      setSecondsRemaining(seconds)

      intervalRef.current = setInterval(() => {
        setSecondsRemaining(prevSeconds => {
          if (prevSeconds <= 1) {
            clearTimer()
            return 0
          }

          return prevSeconds - 1
        })
      }, 1000)
    },
    [clearTimer],
  )

  const handleRequestCode = useCallback(
    async sendType => {
      const nextDeliveryType = sendType || deliveryType
      const optimisticDelay = delay || delayBeforeCodeResend

      setCode('')
      setCodeInvalid(false)
      setVerificationError('')
      setResendTooFrequentError(false)
      setDeliveryType(nextDeliveryType)
      setRecipientMask(
        getRecipientMaskByDeliveryType({
          deliveryType: nextDeliveryType,
          recipientMask,
          phoneMask,
          emailMask,
        }),
      )
      setShowDeliveryMethods(false)

      if (optimisticDelay > 0) {
        startTimer(optimisticDelay)
      }

      try {
        const { request } = onRequestCode?.(sendType) || {}
        const result = await request

        const resultDelay = Number(result.delay)
        const nextDelay =
          Number.isFinite(resultDelay) && resultDelay > 0 ? resultDelay : optimisticDelay
        const resultDeliveryType = result.deliveryType || nextDeliveryType
        const resultPhoneMask = result.phoneMask || phoneMask
        const resultEmailMask = result.emailMask || emailMask

        setRecipientMask(
          getRecipientMaskByDeliveryType({
            deliveryType: resultDeliveryType,
            recipientMask: result.recipientMask,
            phoneMask: resultPhoneMask,
            emailMask: resultEmailMask,
          }),
        )
        setPhoneMask(resultPhoneMask)
        setEmailMask(resultEmailMask)
        setDelay(nextDelay)
        setDeliveryType(resultDeliveryType)
        setAvailableDeliveryTypes(result.availableDeliveryTypes || [])
        setCanChooseAnotherMethod(Boolean(result.canChooseAnotherMethod))
        if (nextDelay > 0) startTimer(nextDelay)
        return true
      } catch (error) {
        if (isCodeResendTooFrequentError(error)) {
          const errorVars = getVarsFromError(error)
          const nextDelay = getDelayFromError(error) || optimisticDelay
          const resultDeliveryType = errorVars.deliveryType || nextDeliveryType
          const resultPhoneMask = errorVars.phoneMask || phoneMask
          const resultEmailMask = errorVars.emailMask || emailMask

          setCodeInvalid(true)
          setResendTooFrequentError(true)
          setRecipientMask(
            getRecipientMaskByDeliveryType({
              deliveryType: resultDeliveryType,
              recipientMask: errorVars.recipientMask || recipientMask,
              phoneMask: resultPhoneMask,
              emailMask: resultEmailMask,
            }),
          )
          setPhoneMask(resultPhoneMask)
          setEmailMask(resultEmailMask)
          setDeliveryType(resultDeliveryType)
          setDelay(nextDelay)
          if (nextDelay > 0) startTimer(nextDelay)
        }

        return true
      }
    },
    [
      delay,
      deliveryType,
      emailMask,
      onRequestCode,
      phoneMask,
      recipientMask,
      startTimer,
      delayBeforeCodeResend,
    ],
  )

  const handleResend = useCallback(() => {
    return handleRequestCode(resendDeliveryType || deliveryType)
  }, [deliveryType, handleRequestCode, resendDeliveryType])

  const handleChooseDeliveryType = useCallback(
    nextDeliveryType => {
      return handleRequestCode(nextDeliveryType)
    },
    [handleRequestCode],
  )

  useEffect(() => {
    if (requestCodeOnMount || initialCodeAlreadyRequested) {
      return () => {
        clearTimer()
      }
    }

    startTimer(delay)

    return () => {
      clearTimer()
    }
  }, [clearTimer, delay, initialCodeAlreadyRequested, requestCodeOnMount, startTimer])

  useEffect(() => {
    if (requestOnMountHandledRef.current) return

    requestOnMountHandledRef.current = true

    if (shouldRestoreRequestedCode) {
      startTimer(initialSecondsUntilResend)
      return
    }

    if (!requestCodeOnMount) return

    startTimer(delayBeforeCodeResend)

    handleRequestCode(firstDeliveryType).then(success => {
      if (!success) {
        requestOnMountHandledRef.current = false
        clearTimer()
        setSecondsRemaining(0)
      }
    })
  }, [
    clearTimer,
    delayBeforeCodeResend,
    handleRequestCode,
    firstDeliveryType,
    initialSecondsUntilResend,
    requestCodeOnMount,
    shouldRestoreRequestedCode,
    startTimer,
  ])

  const handleCodeConfirm = useCallback(
    async nextCode => {
      if (nextCode.length < codeLength) {
        setCodeInvalid(true)
        return false
      }

      if (isSubmittingRef.current) return false

      isSubmittingRef.current = true
      setIsSubmitting(true)

      try {
        const result = onVerifyCode ? await onVerifyCode(nextCode) : false
        const success = result !== false

        if (!success) {
          setCodeInvalid(true)
        } else if (onVerifySuccess) {
          await onVerifySuccess(result, nextCode)
        }

        return success
      } catch (error) {
        setCode('')
        setCodeInvalid(true)
        setVerificationError(labels.CODE_ERROR || labels.codeError)
        return false
      } finally {
        isSubmittingRef.current = false
        setIsSubmitting(false)
      }
    },
    [codeLength, labels, onVerifyCode, onVerifySuccess],
  )

  const handleOtpChange = useCallback(
    nextCode => {
      const normalizedCode = normalizeVerificationCode(nextCode, codeLength)

      setCode(normalizedCode)
      setCodeInvalid(false)
      setVerificationError('')
      setResendTooFrequentError(false)

      if (normalizedCode.length === codeLength) {
        handleCodeConfirm(normalizedCode).finally()
      }
    },
    [codeLength, handleCodeConfirm],
  )

  const handleInputFallback = useCallback(
    (event, inputIndex = 0) => {
      const inputCode = normalizeVerificationCode(event.currentTarget.value, codeLength)

      if (inputCode.length <= 1) return

      if (inputCode.length >= codeLength) {
        handleOtpChange(inputCode)
        return
      }

      const nextCode = normalizeVerificationCode(code, codeLength).split('')
      inputCode.split('').forEach((digit, offset) => {
        const targetIndex = inputIndex + offset

        if (targetIndex < codeLength) nextCode[targetIndex] = digit
      })

      handleOtpChange(nextCode.join(''))
    },
    [code, codeLength, handleOtpChange],
  )

  const renderTimerText = (
    <>
      {labels?.timerText ||
        labels?.TIMER_TEXT ||
        labels?.codeResend ||
        labels?.PROFILE_EMAIL_CODE_RESEND ||
        labels?.PROFILE_CODE_RESEND ||
        ''}{' '}
      <strong>
        {secondsRemaining} {labels.seconds || labels.CODE_SECONDS}
      </strong>
    </>
  )
  const errorText = resendTooFrequentError
    ? variant === 'popup' || variant === 'emailPopup'
      ? resendTooFrequentPopupText
      : ''
    : verificationError || labels.codeError || labels.CODE_ERROR
  const description = hideDescription
    ? ''
    : renderDescription
      ? renderDescription(recipientMask, { deliveryType })
      : getPhoneVerificationDescription(labels, recipientMask, { deliveryType })
  const resendLabels = {
    email: labels.codeSendEmail || labels.CODE_SEND_EMAIL,
    'voice-call': labels.codeSendVoice || labels.CODE_SEND_VOICE,
    sms: labels.codeSendSms || labels.CODE_SEND_SMS,
  }
  const resendLabelType = resendDeliveryType || deliveryType
  const resendButtonLabel =
    resendLabels[resendLabelType] && !resendLabels[resendLabelType].includes('{labels.')
      ? resendLabels[resendLabelType]
      : labels.codeResend || labels.PROFILE_CODE_RESEND
  const deliveryTypeLabels = {
    email: labels.deliveryEmail || labels.CODE_DELIVERY_EMAIL,
    'voice-call': labels.deliveryVoiceFull || labels.CODE_DELIVERY_VOICE_FULL,
    sms: labels.deliverySms || labels.CODE_DELIVERY_SMS,
  }

  const chooseAnotherMethodLabel =
    labels.chooseAnotherMethodPopup ||
    labels.CODE_PROBLEMS_CONFIRMATION ||
    labels.chooseAnotherMethod ||
    labels.CODE_SELECT_ANOTHER_METHOD

  const showChooseAnotherMethod =
    secondsRemaining === 0 && canChooseAnotherMethod && !resendDeliveryType
  const deliveryPopup = closeHandler => (
    <div className={classNames(cls.deliveryPopup, cls[variant])}>
      <button
        type="button"
        className={cls.deliveryPopupClose}
        onClick={closeHandler}
        aria-label={labels.close || labels.PROFILE_CLOSE}
      >
        ×
      </button>
      <h2 className={cls.deliveryPopupTitle}>
        {labels.deliveryMethodTitle || labels.CODE_DELIVERY_METHOD_TITLE}
      </h2>
      <div className={cls.deliveryPopupList}>
        {availableDeliveryTypes.map(type => (
          <button
            type="button"
            key={type}
            className={cls.deliveryPopupItem}
            onClick={() => handleChooseDeliveryType(type)}
            disabled={isSubmitting}
          >
            <span className={cls.deliveryPopupIcon}>
              {deliveryIconImages[type] ? (
                <img className={cls.deliveryPopupIconImage} src={deliveryIconImages[type]} alt="" />
              ) : (
                <Icon>{deliveryIconNames[type] || 'vpn_key'}</Icon>
              )}
            </span>
            <span className={cls.deliveryPopupText}>
              <span className={cls.deliveryPopupName}>{deliveryTypeLabels[type] || type}</span>
              <span className={cls.deliveryPopupDescription}>
                {type === 'email'
                  ? `${labels.deliveryEmailDescription || labels.CODE_DELIVERY_EMAIL_DESCRIPTION} ${emailMask || recipientMask || ''}`.trim()
                  : `${labels.deliveryPhoneDescription || labels.CODE_DELIVERY_PHONE_DESCRIPTION} ${phoneMask || recipientMask || ''}`.trim()}
              </span>
            </span>
          </button>
        ))}
      </div>
    </div>
  )

  if (showDeliveryMethods && !deliveryMethodsAsModal) {
    return deliveryPopup(onClose || (() => setShowDeliveryMethods(false)))
  }

  return (
    <>
      <form
        noValidate
        className={classNames(cls.form, cls[variant], className)}
        onSubmit={event => event.preventDefault()}
      >
        {onClose ? (
          <button
            type="button"
            className={cls.mobileClose}
            onClick={onClose}
            aria-label={labels.close || labels.PROFILE_CLOSE}
          >
            ×
          </button>
        ) : null}
        <div className={cls.headerBlock}>
          {leadingImageSrc ? (
            <img className={cls.leadingImage} src={leadingImageSrc} alt="" />
          ) : null}
          <div className={cls.header}>
            {labels.codeTitle ? <h2 className={cls.title}>{labels.codeTitle}</h2> : null}
            {description ? <p className={cls.description}>{description}</p> : null}
          </div>
        </div>

        <div className={cls.controlsBlock}>
          <OtpInput
            value={code}
            onChange={handleOtpChange}
            numInputs={codeLength}
            inputType="tel"
            shouldAutoFocus
            skipDefaultStyles
            containerStyle={classNames(cls.inputsRow, codeInvalid && cls.invalid)}
            inputStyle={cls.codeInput}
            renderInput={(inputProps, index) => (
              <input
                {...inputProps}
                id={index === 0 ? textId : undefined}
                inputMode="numeric"
                autoComplete={index === 0 ? 'one-time-code' : 'off'}
                className={classNames(inputProps.className, code[index] && cls.filled)}
                disabled={isSubmitting}
                onInput={event => {
                  inputProps.onInput?.(event)
                  handleInputFallback(event, index)
                }}
              />
            )}
          />

          {codeInvalid && errorText ? <p className={cls.errorText}>{errorText}</p> : null}

          <div className={cls.resendRow}>
            {secondsRemaining > 0 ? (
              <Text className={cls.timer}>{renderTimerText}</Text>
            ) : showChooseAnotherMethod ? (
              <Button
                type="button"
                className={classNames(cls.resendButton, cls.chooseMethodButton)}
                onClick={() => setShowDeliveryMethods(true)}
                disabled={isSubmitting}
              >
                {chooseAnotherMethodLabel}
              </Button>
            ) : (
              <Button
                type="button"
                className={cls.resendButton}
                id={resendButtonId}
                onClick={handleResend}
                disabled={isSubmitting}
              >
                {resendButtonLabel}
              </Button>
            )}
          </div>

          {secondaryActionLabel && onSecondaryAction ? (
            <Button
              type="button"
              variant="OUTLINE"
              className={cls.secondaryActionButton}
              onClick={onSecondaryAction}
              disabled={isSubmitting}
            >
              {secondaryActionLabel}
            </Button>
          ) : null}
        </div>
      </form>
      {deliveryMethodsAsModal ? (
        <ModalFront
          isOpen={showDeliveryMethods}
          onClose={() => setShowDeliveryMethods(false)}
          className={cls.deliveryModal}
          classNameBody={cls.deliveryModalBody}
          mobileSheetProps={{
            detent: 'content-height',
          }}
        >
          {deliveryPopup(() => setShowDeliveryMethods(false))}
        </ModalFront>
      ) : null}
    </>
  )
}
